import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DataSection from './Datasection';

const KeysSection = ({ showNotification }) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    key_type: '',
    key_value: ''
  });
  const [errors, setErrors] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  
  // Refs for input fields
  const keyTypeRef = useRef(null);
  const keyValueRef = useRef(null);

  const columns = [
    { key: 'id', header: 'ID' },
    { key: 'key_type', header: 'Key Type' },
    { key: 'key_value', header: 'Key Value' },
    { key: 'created_at', header: 'Created' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  // Focus input field when modal opens
  useEffect(() => {
    if (isModalOpen && keyTypeRef.current) {
      setTimeout(() => {
        keyTypeRef.current.focus();
      }, 100);
    }
  }, [isModalOpen]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('https://apissc.sainikschoolcadet.com/api/keys');
      // Process data to format dates
      const processedData = response.data.map(item => ({
        ...item,
        created_at: new Date(item.created_at).toLocaleDateString()
      }));
      setData(processedData);
    } catch (error) {
      console.error('Error fetching keys:', error);
      showNotification('Failed to load keys', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      key_type: '',
      key_value: ''
    });
    setErrors({});
    setSelectedItem(null);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      key_type: item.key_type || '',
      key_value: item.key_value || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this key?')) {
      try {
        await axios.delete(`https://apissc.sainikschoolcadet.com/api/keys/${id}`);
        setData(data.filter(item => item.id !== id));
        showNotification('Key deleted successfully');
      } catch (error) {
        console.error('Error deleting key:', error);
        showNotification('Failed to delete key', 'error');
      }
    }
  };

  const handleAdd = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Maintain cursor position in textarea
    if (name === 'key_value' && keyValueRef.current) {
      const cursorPos = e.target.selectionStart;
      setTimeout(() => {
        keyValueRef.current.selectionStart = cursorPos;
        keyValueRef.current.selectionEnd = cursorPos;
      }, 0);
    }
  };

  const handleKeyDown = (e) => {
    // Prevent default tab behavior and manage focus manually
    if (e.key === 'Tab' && !e.shiftKey) {
      if (e.target === keyTypeRef.current) {
        e.preventDefault();
        keyValueRef.current.focus();
      }
    } else if (e.key === 'Tab' && e.shiftKey) {
      if (e.target === keyValueRef.current) {
        e.preventDefault();
        keyTypeRef.current.focus();
      }
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.key_type.trim()) {
      newErrors.key_type = 'Key type is required';
    }
    
    if (!formData.key_value.trim()) {
      newErrors.key_value = 'Key value is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setFormLoading(true);
    
    try {
      let response;
      
      const submitData = {
        key_type: formData.key_type,
        key_value: formData.key_value
      };
      
      if (selectedItem) {
        // Update existing item
        response = await axios.put(
          `https://apissc.sainikschoolcadet.com/api/keys/${selectedItem.id}`, 
          submitData
        );
      } else {
        // Create new item
        response = await axios.post(
          'https://apissc.sainikschoolcadet.com/api/keys',
          submitData
        );
      }
      
      if (response.data) {
        showNotification(selectedItem ? 'Key updated successfully' : 'Key added successfully');
        setIsModalOpen(false);
        fetchData();
      }
    } catch (error) {
      console.error('Error saving key:', error);
      if (error.response && error.response.data && error.response.data.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: 'Failed to save. Please try again.' });
      }
      showNotification('Failed to save key', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  // Handle click outside of modal to close it
  const handleModalClick = (e) => {
    if (e.target.classList.contains('modal-backdrop')) {
      closeModal();
    }
  };

  // Modal component
  const FormModal = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null;
    
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 modal-backdrop"
        onClick={handleModalClick}
      >
        <div 
          className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    );
  };

  return (
    <>
      <DataSection
        title="Keys Management"
        data={data}
        columns={columns}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
        isLoading={isLoading}
        emptyMessage="No keys found"
      />
      
      <FormModal isOpen={isModalOpen} onClose={closeModal}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <h2 className="text-xl font-bold mb-4">
            {selectedItem ? 'Edit Key' : 'Add New Key'}
          </h2>
          
          <div className="form-group">
            <label htmlFor="key_type" className="block mb-1 font-medium">
              Key Type *
            </label>
            <input
              type="text"
              id="key_type"
              name="key_type"
              ref={keyTypeRef}
              value={formData.key_type}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              className={`w-full p-2 border rounded ${errors.key_type ? 'border-red-500' : 'border-gray-300'}`}
              required
              autoFocus
            />
            {errors.key_type && (
              <p className="text-red-500 text-sm mt-1">{errors.key_type}</p>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="key_value" className="block mb-1 font-medium">
              Key Value *
            </label>
            <textarea
              id="key_value"
              name="key_value"
              ref={keyValueRef}
              value={formData.key_value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              className={`w-full p-2 border rounded font-mono ${errors.key_value ? 'border-red-500' : 'border-gray-300'}`}
              rows="4"
              spellCheck="false"
              autoComplete="off"
              required
            />
            {errors.key_value && (
              <p className="text-red-500 text-sm mt-1">{errors.key_value}</p>
            )}
          </div>
          
          {errors.general && (
            <div className="bg-red-100 text-red-700 p-3 rounded">
              {errors.general}
            </div>
          )}
          
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              disabled={formLoading}
            >
              Cancel
            </button>
            
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              disabled={formLoading}
            >
              {formLoading ? (
                <span>Saving...</span>
              ) : (
                <span>{selectedItem ? 'Update' : 'Save'}</span>
              )}
            </button>
          </div>
        </form>
      </FormModal>
    </>
  );
};

export default KeysSection;